import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getRequisitions, deleteRequisition } from '../../../models/requisitionModel';
import { getSettings } from '../../../models/settingsModel';
import { toast } from 'react-toastify';

function RequisitionsList({ status = 'pending' }) {
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(() => {
    // Try to restore page from localStorage on mount
    const savedPage = localStorage.getItem('requisitionListPage');
    return savedPage ? parseInt(savedPage, 10) : 1;
  });
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [showPendingApproval, setShowPendingApproval] = useState(false);
  const [isRefreshingApprovals, setIsRefreshingApprovals] = useState(false);
  const [sortField, setSortField] = useState('requisition_id');
  const [sortDirection, setSortDirection] = useState('desc');
  const [globalSettings, setGlobalSettings] = useState({ site_limit: 20000, site_limit_above: '1' });
  const { can, user, isMasquerading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Track previous status to detect actual changes
  const [prevStatus, setPrevStatus] = useState(null);
  const [prevSearch, setPrevSearch] = useState('');
  
  // Load global settings when component mounts
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await getSettings();
        if (response.status === 1 && response.data) {
          setGlobalSettings(response.data);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    
    loadSettings();
  }, []);
  
  // Helper function to determine if current user is a buyer (role 15 or department 8)
  const isBuyer = () => { 
    if (!user) return false;
    const roleCheck = user.role === 15 || user.role === '15';
    const deptCheck = user.department === 8 || user.department === '8'; 
    return roleCheck || deptCheck;
  };
  
  // Helper function to determine if current user is an approver
  const isApprover = () => {
    if (!user || !user.user_id) return false;
    // Check if user has any requisitions waiting for their approval
    return pendingApprovalCount > 0;
  };
  
  // Get effective user ID for filtering (handles masquerading)
  const getEffectiveUserId = () => {
    return user?.user_id;
  };
  
  // Get effective user name for display (handles masquerading)
  const getEffectiveUserName = () => {
    return user?.user_full_name || user?.name || 'Unknown User';
  };

  // Check if requisition is ready for approval (has all prerequisites)
  const isRequisitionReady = (req) => {
    const hasQuote = !!(req.requisition_quote && req.requisition_quote !== '');
    
    // Check evaluation - Backend provides unserialized evaluation data
    const evalValue = req.quotation_evaluation;
    const hasEvaluation = !!(
      evalValue != null &&  // Check null/undefined first
      evalValue !== '' && 
      evalValue !== 'null' &&
      evalValue !== '0' &&
      (typeof evalValue === 'string' ? evalValue.trim().length > 0 : true)
    );
    
    const hasDeclaration = !!(req.requisition_declaration && req.requisition_declaration !== '' && req.requisition_declaration !== null);
    
    // ALL three prerequisites are required: quote AND evaluation AND declaration
    return hasQuote && hasEvaluation && hasDeclaration;
  };

  const normalizeUserId = (value) => {
    if (value === null || value === undefined) return null;
    const raw = typeof value === 'object' ? (value.user_id ?? value.id) : value;
    if (raw === null || raw === undefined) return null;
    const num = Number(raw);
    if (!Number.isFinite(num) || num <= 0) return null;
    return String(num);
  };

  // Check if current user is the approver at this stage.
  // Delegation is a replacement: if an approver has a delegate, ONLY the delegate should approve.
  const isUserCurrentApprover = (req) => {
    if (!user || !user.user_id) return false;
    
    const userId = normalizeUserId(user.user_id);
    if (!userId) return false;
    const reqStatus = req.requisition_status;
    
    // Check if it's the user's turn based on status
    if (reqStatus === 0) {
      // First approval stage
      const delegatedTo = normalizeUserId(req.mid_delegated_to_user_id) ?? normalizeUserId(req.mid_delegate);
      const effectiveApprover = normalizeUserId(req.mid_effective_user_id) ?? delegatedTo ?? normalizeUserId(req.mid);
      return !!effectiveApprover && effectiveApprover === userId;
    } else if (reqStatus === 1) {
      // Second approval stage
      const delegatedTo = normalizeUserId(req.second_delegated_to_user_id) ?? normalizeUserId(req.second_delegate);
      const effectiveApprover = normalizeUserId(req.second_effective_user_id) ?? delegatedTo ?? normalizeUserId(req.second);
      return !!effectiveApprover && effectiveApprover === userId;
    } else if (reqStatus === 2 && req.specification_approvals >= 3) {
      // Third approval stage (only if required)
      // Check if quote exceeds site limit to determine approver type (using net amount)
      const quotationTotal = parseFloat(req.quotation_total?.toString().replace(/[^\d.-]/g, '') || 0);
      const quotationVat = parseFloat(req.quotation_vat?.toString().replace(/[^\d.-]/g, '') || 0);
      const netAmount = quotationTotal - quotationVat;
      const siteLimit = parseFloat(globalSettings.site_limit || 20000);
      
      if (netAmount > siteLimit) {
        // High value: SAP approver
        const delegatedTo = normalizeUserId(req.sap_delegated_to_user_id) ?? normalizeUserId(req.sap_delegate);
        const effectiveApprover = normalizeUserId(req.sap_effective_user_id) ?? delegatedTo ?? normalizeUserId(req.approval);
        return !!effectiveApprover && effectiveApprover === userId;
      } else {
        // Standard value: PM role (role 4) with delegation support
        const delegatedTo = normalizeUserId(req.pm_delegated_to_user_id) ?? normalizeUserId(req.pm_delegate);
        const effectiveApprover = normalizeUserId(req.pm_effective_user_id) ?? delegatedTo ?? (user.role === 4 || user.role === '4' ? normalizeUserId(user.user_id) : null);
        return !!effectiveApprover && effectiveApprover === userId;
      }
    }
    
    return false;
  };

  // Check if approver is waiting for more information from requester
  const isApproverWaitingForInfo = (req) => {
    if (!user || !user.user_id) return false;
    
    // Must be the current approver for this to matter
    if (!isUserCurrentApprover(req)) return false;
    
    // Check if there's a last comment by this approver
    const userId = String(user.user_id);
    const lastCommentUser = req.last_comment_user ? String(req.last_comment_user) : null;
    const lastCommentApproved = req.last_comment_approved;
    const lastCommentText = req.last_comment_text || '';
    
    // Return true if:
    // 1. Last comment was made by current user (approver)
    // 2. Last comment was NOT an approval (comment_approved = 0)
    // 3. Last comment was NOT a rejection (doesn't contain rejection keywords)
    if (lastCommentUser === userId && 
        lastCommentApproved === 0 && 
        !lastCommentText.toLowerCase().includes('rejection reason:') &&
        !lastCommentText.toLowerCase().includes('rejected')) {
      return true;
    }
    
    return false;
  };

  // Filter requisitions by workflow stage (client-side)
  const filterByWorkflowStage = (requisitions, stage) => {
    if (!stage || stage === 'all') return requisitions;
    
    return requisitions.filter(req => {
      const workflowStage = getWorkflowStage(req);
      return workflowStage === stage;
    });
  };

  // Fetch requisitions - SIMPLE LOGIC MATCHING ORIGINAL PHP
  const fetchRequisitions = useCallback(async () => {
    setLoading(true);
    try {
      const effectiveStatus = getEffectiveStatus();
      const params = {
        page: currentPage,
        pageSize: pageSize,
      };
      
      if (searchTerm) params.filter = searchTerm;
      
      // === ORIGINAL PHP LOGIC (SIMPLE!) ===
      // 1. Backend always returns ALL requisitions for the user
      // 2. Frontend filters based on ?ready= parameter:
      //    - If ?ready= is set: Show ONLY ready items at user's stage
      //    - If no ?ready=: Show ALL items
      
      // Standard status filtering - includes all approver-specific filters
      const serverSideStatuses = ['all', 'pending', 'approved', 'junked', 'archived', 'purchase_order', 'my_assigned', 'assigned', 'awaiting_my_approval', 'awaiting_my_pm_approval', 'awaiting_my_approvals'];
      if (serverSideStatuses.includes(effectiveStatus)) {
        params.status = effectiveStatus;
      } else {
        // For workflow stages, determine which server-side status to use
        // buyer_assignment and assigned stages require APPROVED requisitions (status >= specification_approvals)
        // Other workflow stages (quotation, evaluation, declaration, first/second/third approval) are pre-approval stages
        const postApprovalStages = ['buyer_assignment', 'assigned', 'purchase_order', 'completed'];
        if (postApprovalStages.includes(effectiveStatus)) {
          params.status = 'approved';
        } else {
          params.status = 'pending';
        }
      }
       
      const response = await getRequisitions(params);
      
      if (response.status === 1 && response.data) {
        let responseData = response.data.data || [];
        
        // === STEP 1: Handle count based on status ===
        if (effectiveStatus === 'awaiting_my_approvals') {
          // Use the same total count as pagination (model-normalized)
          const totalFromBackend =
            response.data.meta?.totalItems ||
            response.data.data?.total ||
            response.data.total ||
            responseData.length;
          setPendingApprovalCount(totalFromBackend || 0);
        } else {
          // Calculate "ready" count client-side for other statuses
          let readyList = [];
          if (user && user.user_id) {
            readyList = responseData.filter(req => {
              return isUserCurrentApprover(req) && 
                     isRequisitionReady(req) && 
                     req.requisition_status < (req.specification_approvals || 1);
            });
            setPendingApprovalCount(readyList.length);
          } else {
            setPendingApprovalCount(0);
          }
        }
        
        // === STEP 2: Apply client-side workflow stage filtering if needed ===
        const workflowStages = ['quotation', 'evaluation', 'declaration', 
                                'first_approval', 'second_approval', 'third_approval', 
                                'buyer_assignment', 
                                'completed', 'rejected'];
        
        if (workflowStages.includes(effectiveStatus)) {
          responseData = filterByWorkflowStage(responseData, effectiveStatus);
        }
        
 
        
        setRequisitions(responseData);
        
      
        // For client-side filtered cases, use the filtered count; for server-side, use backend total
        const clientFiltered = workflowStages.includes(effectiveStatus);
        // The model transforms the response to have total in meta.totalItems
        const totalFromBackend = response.data.meta?.totalItems || response.data.data?.total || response.data.total || responseData.length;
         setTotalCount(clientFiltered ? responseData.length : totalFromBackend);
        
      } else {
        console.error('Failed API response:', response);
        toast.error(response.message || 'Failed to load requisitions');
        setRequisitions([]);
        setTotalCount(0);
        setPendingApprovalCount(0);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('An error occurred while loading requisitions');
      setRequisitions([]);
      setTotalCount(0);
      setPendingApprovalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, status, user, location.search]);

  // Handle column sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending as default
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort requisitions in memory
  const sortedRequisitions = React.useMemo(() => {
    if (!sortField) return requisitions;

    return [...requisitions].sort((a, b) => {
      let aValue, bValue;

      // Special handling for supplier field
      if (sortField === 'supplier') {
        // Match the display logic for supplier
        aValue = 'None';
        if (a.quotation_company_name && a.quotation_company_name !== 'Not Selected' && !a.quotation_company_name.startsWith('Supplier ID:')) {
          aValue = a.quotation_company_name;
        } else if (a.company_name_actual && a.company_name_actual !== 'Not Selected' && !a.company_name_actual.startsWith('Supplier ID:')) {
          aValue = a.company_name_actual;
        }

        bValue = 'None';
        if (b.quotation_company_name && b.quotation_company_name !== 'Not Selected' && !b.quotation_company_name.startsWith('Supplier ID:')) {
          bValue = b.quotation_company_name;
        } else if (b.company_name_actual && b.company_name_actual !== 'Not Selected' && !b.company_name_actual.startsWith('Supplier ID:')) {
          bValue = b.company_name_actual;
        }
      } else {
        aValue = a[sortField];
        bValue = b[sortField];
      }

      // Handle null/undefined values
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      // Convert to lowercase for string comparison
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [requisitions, sortField, sortDirection]);

  useEffect(() => {
    fetchRequisitions();
  }, [fetchRequisitions]);

  // Listen for approval events to refresh the count
  useEffect(() => {
    const handleRequisitionApproval = () => { 
      setIsRefreshingApprovals(true);
      fetchRequisitions().finally(() => {
        setTimeout(() => setIsRefreshingApprovals(false), 1000); // Show indicator for 1 second
      });
    };

    // Listen for custom approval events
    window.addEventListener('requisitionApproved', handleRequisitionApproval);
    window.addEventListener('requisitionRejected', handleRequisitionApproval);
    
    return () => {
      window.removeEventListener('requisitionApproved', handleRequisitionApproval);
      window.removeEventListener('requisitionRejected', handleRequisitionApproval);
    };
  }, [fetchRequisitions]);

  // Auto-refresh pending approval count every 2 minutes to catch approvals from other sources
  useEffect(() => {
    if (!showPendingApproval) {
      const interval = setInterval(() => { 
        fetchRequisitions();
      }, 120000); // 2 minutes

      return () => clearInterval(interval);
    }
  }, [fetchRequisitions, showPendingApproval]);

  // Save current page to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('requisitionListPage', currentPage.toString());
  }, [currentPage]);

  // Reset to page 1 ONLY when search or status actually changes (not on mount/remount)
  useEffect(() => {
    const currentStatus = showPendingApproval;
    
    // Check if search or status actually changed
    if (prevSearch !== searchTerm && prevSearch !== '') {
      setCurrentPage(1);
    } else if (prevStatus !== null && prevStatus !== currentStatus) {
      setCurrentPage(1);
    }
    
    // Update previous values
    setPrevSearch(searchTerm);
    setPrevStatus(currentStatus);
  }, [searchTerm, showPendingApproval]);
  
  // Check for "awaiting_my_approvals" status in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlStatus = params.get('status');
    
    if (urlStatus === 'awaiting_my_approvals') {
      setShowPendingApproval(true);
    } else {
      setShowPendingApproval(false);
    }
  }, [location.search]);

  // Get effective status from URL params, default to 'all' for everyone
  const getEffectiveStatus = () => {
    const params = new URLSearchParams(location.search);
    const urlStatus = params.get('status');
    
    // Treat status=0 as 'all'
    if (urlStatus === '0') {
      return 'all';
    }
    
    // Default to 'all' if no URL status is set
    return urlStatus || 'all';
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const formatDate = (timestamp) => {
    if (!timestamp || timestamp === null || timestamp === '0' || timestamp === 0) return 'N/A';
    
    // If it's already in YYYY-MM-DD format, convert to display format
    if (typeof timestamp === 'string' && timestamp.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB');
      }
    }
    
    // Handle numeric timestamps
    const date = Number.isInteger(parseInt(timestamp))
      ? new Date(parseInt(timestamp) * 1000)
      : new Date(timestamp);
      
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    // Check if it's the Unix epoch (1970-01-01) which indicates invalid data
    if (date.getFullYear() === 1970 && date.getMonth() === 0 && date.getDate() === 1) {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-GB');
  };

  // Get current workflow stage - FIXED to check approval status FIRST
  // Helper function to check if PM approval is required
  const requiresPMApproval = (req) => {
    if (!req) return false;
    
    // CRITICAL: If PM has already approved, PM approval is NOT needed anymore
    // Check this FIRST before any other conditions
    if (req.requisition_pm_approved == 1 || req.requisition_pm_approved === '1' || req.requisition_pm_approved === true) {
      return false;
    }
    
    // Use global settings instead of req.site_limit_above (which isn't returned by backend)
    const siteLimitAbove = globalSettings.site_limit_above === '1' || globalSettings.site_limit_above === 1;
    if (!siteLimitAbove) return false;
    
    // FIXED: Use submission_count from backend to determine if there's only one quote
    // This matches the logic in ViewRequisition.jsx: hasOneSubmission = requisition.submissions?.length === 1
    const submissionCount = parseInt(req.submission_count || 0);
    const hasOneSubmission = submissionCount === 1;
    const isEmergency = req.specification_emergency == 1;
    
    // Must have only one submission OR be emergency
    if (!hasOneSubmission && !isEmergency) return false;
    
    // Get the limit and total (NET amount excluding VAT)
    const siteLimit = parseFloat(req.site_limit || globalSettings.site_limit || 20000);
    const quotationTotal = req.quotation_total ? 
      parseFloat(req.quotation_total.toString().replace(/\s+/g, '').replace(/,/g, '')) : 0;
    const quotationVat = req.quotation_vat ? 
      parseFloat(req.quotation_vat.toString().replace(/\s+/g, '').replace(/,/g, '')) : 0;
    
    // Calculate net amount (total minus VAT)
    const netAmount = quotationTotal - quotationVat;
    
    // Must be above the limit (using net amount)
    if (netAmount < siteLimit) return false;
    
    return true;
  };

  const getWorkflowStage = (req) => {
    // Check special states FIRST based on requisition_state field
    // requisition_state = 1 means JUNKED (not archived!)
    if (req.requisition_state == 1) {
      return 'junked';
    }
    
    // requisition_state = 7 means ARCHIVED
    if (req.requisition_state == 7) {
      return 'archived';
    }
    
    // requisition_status = 4 means REJECTED
    if (req.requisition_status === 4) {
      return 'rejected';
    }
    
    // Check if PO is issued (completion) - requisition_po_date > 0
    if (req.requisition_po_date && req.requisition_po_date > 0) {
      return 'completed';
    }
    
    const specApprovals = req.specification_approvals || 2;
    
    // CHECK APPROVAL STATUS FIRST - If fully approved, skip prerequisite checks
    if (req.requisition_status >= specApprovals) {
      // Post-approval stages - requisition is APPROVED
      if (req.requisition_assign_id && req.requisition_assign_id > 0) {
        // Buyer is assigned (check requisition_assign_id, not assigned field)
        if (req.requisition_sap_approve_time && req.requisition_sap_approve_time > 0) {
          return 'purchase_order'; // SAP draft requested, waiting for PO
        } else {
          return 'assigned'; // Assigned to buyer, no SAP draft yet
        }
      } else {
        return 'buyer_assignment'; // Waiting for buyer assignment
      }
    }
    
    // Not yet fully approved - check prerequisites and approval stages
    const hasQuote = !!(req.requisition_quote && req.requisition_quote !== '');
    
    // Check evaluation - Backend provides unserialized evaluation data
    const evalValue = req.quotation_evaluation;
    const hasEvaluation = !!(
      evalValue != null &&  // Check null/undefined first
      evalValue !== '' && 
      evalValue !== 'null' &&
      evalValue !== '0' &&
      (typeof evalValue === 'string' ? evalValue.trim().length > 0 : true)
    );
    
    const hasDeclaration = !!(req.requisition_declaration && req.requisition_declaration !== '' && req.requisition_declaration !== null);
    
    // If missing any prerequisite, it's in draft/preparation stages
    if (!hasQuote) {
      return 'quotation';
    }
    
    // Check if evaluation is missing - show status based on actual state
    // All requisitions must have evaluation before declaration
    if (!hasEvaluation) {
      return 'evaluation';
    }
    
    if (!hasDeclaration) {
      return 'declaration';
    }
    
    // All prerequisites met - NOW check if PM approval is blocking (at first approval stage only)
    const reqStatus = Number(req.requisition_status);
    const needsPMApproval = requiresPMApproval(req);
    
    
    // PM approval only blocks at first approval stage (status 0)
    if (needsPMApproval && reqStatus === 0) {
      return 'pm_approval';
    }
    
    // All prerequisites met - in approval stages
    if (reqStatus === 0) {
      return 'first_approval';
    } else if (reqStatus === 1) {
      return 'second_approval';
    } else if (reqStatus === 2) {
      if (specApprovals >= 3) {
        return 'third_approval';
      } else {
        // Status 2 but only 2 approvals needed = fully approved
        return 'buyer_assignment';
      }
    }
    
    // Fallback
    return 'draft';
  };

  const getStatusDisplay = (req) => {
    const workflowStage = getWorkflowStage(req);
    
    // Check if approver is waiting for more info - this takes precedence
    if (isApproverWaitingForInfo(req)) {
      return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-blue-100 text-blue-800 border border-blue-300">
        <i className="fas fa-clock mr-1"></i>Awaiting Response
      </span>;
    }
    
    // PM Approval takes precedence - show PM Approval badge even if user is the PM
    if (workflowStage === 'pm_approval') {
      return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-purple-200 text-purple-900">PM Approval</span>;
    }
    
    // Check if user needs to approve this requisition (at their stage AND ready)
    const needsUserApproval = isUserCurrentApprover(req) && isRequisitionReady(req);
    
    // Override with user-specific message if needed
    if (needsUserApproval) {
      return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-red-100 text-red-800">Needs Your Approval</span>;
    }
    
    // Return styled badge based on workflow stage
    switch (workflowStage) {
      case 'archived':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-gray-200 text-gray-700">Archived</span>;
      case 'junked':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-gray-400 text-white">Junked</span>;
      case 'completed':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-green-100 text-green-800">Completed (PO Issued)</span>;
      case 'purchase_order':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-blue-100 text-blue-800">Awaiting PO</span>;
      case 'assigned':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-purple-100 text-purple-800">Assigned (No SAP Draft)</span>;
      case 'buyer_assignment':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-indigo-100 text-indigo-800">Buyer Assignment</span>;
      case 'pm_approval':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-purple-200 text-purple-900">PM Approval</span>;
      case 'third_approval':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-yellow-100 text-yellow-800">Third Approval</span>;
      case 'second_approval':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-yellow-100 text-yellow-800">Second Approval</span>;
      case 'first_approval':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-yellow-100 text-yellow-800">First Approval</span>;
      case 'declaration':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-orange-100 text-orange-800">Awaiting Declaration</span>;
      case 'evaluation':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-orange-100 text-orange-800">Awaiting Evaluation</span>;
      case 'quotation':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-orange-100 text-orange-800">Awaiting Quotation</span>;
      case 'rejected':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-red-100 text-red-800">Rejected</span>;
      case 'draft':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-gray-100 text-gray-800">Draft</span>;
      default:
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-blue-100 text-blue-800">Pending</span>;
    }
  };

  // Only show full-page spinner on initial load (no data ever loaded)
  // After initial load, show inline loading indicator while keeping data visible
  if (loading && requisitions.length === 0 && !isRefreshingApprovals) {
    return (
      <div className="p-6 md:p-10 flex justify-center items-center h-[calc(100vh-150px)]">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 py-1">
      {/* Professional Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
        <div className="flex items-start justify-between">
          {/* Left: Icon, Title, Description, Results */}
          <div className="flex items-start">
            <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
              <svg className="h-5 w-5" fill="none" stroke="#0096DE" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">
                {showPendingApproval ? 'Ready for Approval' : 
                 (getEffectiveStatus() === 'my_assigned' ? 'My Assigned Requisitions' : 
                  getEffectiveStatus() === 'assigned' ? 'All Assigned Requisitions' : 'Requisitions')}
              </h1>
              <p className="text-xs text-gray-500 mb-1">
                {showPendingApproval ? 'Requisitions ready for your approval' :
                 getEffectiveStatus() === 'my_assigned' ? 
                 `Requisitions assigned to ${getEffectiveUserName()}` : 
                 getEffectiveStatus() === 'assigned' ?
                 'All requisitions assigned to buyers' :
                 'Purchase Requests & Orders'}
              </p>
              <div className="flex items-center mt-1">
                <span className="text-xs font-medium" style={{ color: '#0096DE' }}>
                  {totalCount ? `Showing ${Math.min(pageSize, totalCount)} of ${totalCount} results` : '0 results'}
                  {(searchTerm || (status && status !== 'pending') || showPendingApproval || getEffectiveStatus() === 'my_assigned' || getEffectiveStatus() === 'assigned') && (
                    <span className="ml-1 text-blue-600">
                      (filtered)
                    </span>
                  )}
                </span>
                {isRefreshingApprovals && (
                  <span className="ml-2 flex items-center text-xs text-green-600">
                    <svg className="animate-spin h-3 w-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Right: All Controls and Search */}
          <div className="flex flex-col gap-2 items-end min-w-0">
            {/* Row 1: All Controls on Right */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Clear Filters Button (only show if filters are active) */}
              {(searchTerm || (status && status !== 'pending') || showPendingApproval || getEffectiveStatus() === 'my_assigned') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    // For buyers, navigate to their default view (my_assigned), for others go to general pending
                    if (isBuyer()) {
                      navigate('/dashboard/requisitions?status=my_assigned');
                    } else {
                      navigate('/dashboard/requisitions');
                    }
                  }}
                  className="inline-flex items-center px-2 py-1.5 border border-red-300 rounded-md shadow-sm text-xs text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-1 focus:ring-red-500 flex-shrink-0"
                >
                  Clear Filters
                </button>
              )}
              
              {/* Refresh Button */}
              <button 
                onClick={() => fetchRequisitions()} 
                className={`inline-flex items-center px-2 py-1.5 border border-gray-300 shadow-sm text-sm rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0 ${
                  loading ? 'text-blue-600 bg-blue-50 cursor-wait' : 'text-gray-700 bg-white hover:bg-gray-50'
                }`}
                title={loading ? "Refreshing..." : "Refresh"}
                disabled={loading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              
              {/* Status Filter */}
              <div className="flex-shrink-0">
                <select
                  value={showPendingApproval ? "ready" : getEffectiveStatus()}
                  onChange={(e) => {
                    if (e.target.value === "ready") {
                      navigate(`/dashboard/requisitions?status=awaiting_my_approvals`);
                    } else if (e.target.value === "pending") {
                      navigate(`/dashboard/requisitions?status=pending`);
                    } else if (e.target.value === "all") {
                      navigate(`/dashboard/requisitions?status=all`);
                    } else {
                      navigate(`/dashboard/requisitions?status=${e.target.value}`);
                    }
                  }}
                  className={`text-sm px-2 py-1.5 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    (showPendingApproval || (status && status !== 'pending') || getEffectiveStatus() === 'my_assigned') ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white'
                  }`}
                >
                  {/* Show My Assigned option for buyers */}
                  {isBuyer() && (
                    <option value="my_assigned">My Assigned Requisitions</option>
                  )}
                  
                  {/* Show All Assigned option for buyers */}
                  {isBuyer() && (
                    <option value="assigned">All Assigned Requisitions</option>
                  )}
                  
                  {/* Show Ready for Approval option for non-procurement users (always visible) */}
                  {user && !isBuyer() && (
                    <option value="ready">Needs Your Approval {pendingApprovalCount > 0 ? `(${pendingApprovalCount})` : ''}</option>
                  )}
                  
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  
                  {/* PM Approval Filter - Only for Procurement Managers (role 4) */}
                  {user && (user.role === 4 || user.role === '4') && (
                    <option value="awaiting_my_pm_approval">Awaiting My PM Approval</option>
                  )}
                  
                  {/* Workflow Stage Filters */}
                  <option value="quotation">Awaiting Quotation</option>
                  <option value="evaluation">Awaiting Evaluation</option>
                  <option value="declaration">Awaiting Declaration</option>
                  <option value="first_approval">Awaiting First Approval</option>
                  <option value="second_approval">Awaiting Second Approval</option>
                  <option value="third_approval">Awaiting Third Approval</option>
                  <option value="buyer_assignment">Awaiting Buyer Assignment</option>
                  <option value="assigned">Assigned (No SAP Draft)</option>
                  
                  {/* Final States */}
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="completed">Completed (PO Issued)</option>
                  <option value="junked">Junk</option>
                  <option value="archived">Archived</option>
                  <option value="purchase_order">Purchase Order</option>
                </select>
              </div>

              {/* Page Size Filter */}
              <div className="flex-shrink-0">
                <select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="text-sm px-2 py-1.5 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[10, 20, 30, 50].map(size => (
                    <option key={size} value={size}>{size} per page</option>
                  ))}
                </select>
              </div>

              {/* Create Button */}
              {can('Create Requisition') && (
                <Link
                  to="/dashboard/specifications"
                  className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm text-white focus:outline-none focus:ring-1 flex-shrink-0"
                  style={{ backgroundColor: '#0096DE', boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)', borderColor: '#0096DE' }}
                >
                  <svg className="-ml-0.5 mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  New Requisition
                </Link>
              )}
            </div>
            
            {/* Search Box in Row 2 */}
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search requisitions..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Banner removed - count is displayed in dropdown and managed via backend */}
      
      {/* Simple Table */}
      <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
              <tr>
                <th 
                  className="px-3 py-2 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-100 transition-colors select-none" 
                  style={{ color: '#0087D4' }}
                  onClick={() => handleSort('requisition_id')}
                >
                  <div className="flex items-center gap-1">
                    ID
                    {sortField === 'requisition_id' && (
                      <span className="text-blue-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-100 transition-colors select-none" 
                  style={{ color: '#0087D4' }}
                  onClick={() => handleSort('requisition_number')}
                >
                  <div className="flex items-center gap-1">
                    Req Number
                    {sortField === 'requisition_number' && (
                      <span className="text-blue-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-100 transition-colors select-none" 
                  style={{ color: '#0087D4' }}
                  onClick={() => handleSort('specification_title')}
                >
                  <div className="flex items-center gap-1">
                    Project Name
                    {sortField === 'specification_title' && (
                      <span className="text-blue-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-100 transition-colors select-none" 
                  style={{ color: '#0087D4' }}
                  onClick={() => handleSort('user_full_name')}
                >
                  <div className="flex items-center gap-1">
                    User
                    {sortField === 'user_full_name' && (
                      <span className="text-blue-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-100 transition-colors select-none" 
                  style={{ color: '#0087D4' }}
                  onClick={() => handleSort('department_name')}
                >
                  <div className="flex items-center gap-1">
                    Department
                    {sortField === 'department_name' && (
                      <span className="text-blue-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-100 transition-colors select-none" 
                  style={{ color: '#0087D4' }}
                  onClick={() => handleSort('requisition_date')}
                >
                  <div className="flex items-center gap-1">
                    Created
                    {sortField === 'requisition_date' && (
                      <span className="text-blue-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-100 transition-colors select-none" 
                  style={{ color: '#0087D4' }}
                  onClick={() => handleSort('supplier')}
                >
                  <div className="flex items-center gap-1">
                    Supplier
                    {sortField === 'supplier' && (
                      <span className="text-blue-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-100 transition-colors select-none" 
                  style={{ color: '#0087D4' }}
                  onClick={() => handleSort('quotation_total')}
                >
                  <div className="flex items-center gap-1">
                    Value
                    {sortField === 'quotation_total' && (
                      <span className="text-blue-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-100 transition-colors select-none" 
                  style={{ color: '#0087D4' }}
                  onClick={() => handleSort('requisition_status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {sortField === 'requisition_status' && (
                      <span className="text-blue-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && sortedRequisitions.length === 0 && (
                <tr>
                  <td colSpan="10" className="px-3 py-4 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm">Loading...</span>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && sortedRequisitions.length === 0 && (
                <tr>
                  <td colSpan="10" className="px-3 py-6 text-center">
                    <p className="text-gray-600">
                      {showPendingApproval 
                        ? "No requisitions require your approval at this time" 
                        : "No requisitions found"}
                    </p>
                  </td>
                </tr>
              )}
              {sortedRequisitions.map(req => {
                const displayUser = req.user_full_name || req.requester_name || (req.user_id ? `User #${req.user_id}` : 'Unknown');
                
                // Match the original PHP logic: only show actual supplier names, otherwise "None"
                let displaySupplier = 'None';
                if (req.quotation_company_name && req.quotation_company_name !== 'Not Selected' && !req.quotation_company_name.startsWith('Supplier ID:')) {
                  displaySupplier = req.quotation_company_name;
                } else if (req.company_name_actual && req.company_name_actual !== 'Not Selected' && !req.company_name_actual.startsWith('Supplier ID:')) {
                  displaySupplier = req.company_name_actual;
                }

                // Format the selected quote total value (minus VAT)
                const formatQuoteValue = (quotationTotal, quotationVat) => {
                  if (!quotationTotal) return 'No value';
                  
                  // Handle both string and number formats, remove spaces and commas
                  const cleanTotal = typeof quotationTotal === 'string' 
                    ? quotationTotal.replace(/[\s,]/g, '') 
                    : quotationTotal.toString();
                  
                  const cleanVat = quotationVat 
                    ? (typeof quotationVat === 'string' ? quotationVat.replace(/[\s,]/g, '') : quotationVat.toString())
                    : '0';
                  
                  const totalAmount = parseFloat(cleanTotal) || 0;
                  const vatAmount = parseFloat(cleanVat) || 0;
                  const amount = totalAmount - vatAmount;
                  
                  return amount > 0 
                    ? `R${amount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` 
                    : 'No value';
                };
                
                return (
                  <tr key={req.requisition_id} 
                      className={`${
                        isApproverWaitingForInfo(req) 
                          ? "bg-yellow-100 border-l-8 border-orange-500 shadow-md hover:bg-yellow-200 ring-1 ring-orange-200" 
                          : "hover:bg-blue-50"
                      }`}>
                    <td className="px-3 py-2 text-sm">{req.requisition_id}</td>
                    <td className="px-3 py-2 text-sm">EPR-{req.requisition_number}</td>
                    <td className="px-3 py-2 text-sm">{req.specification_title || 'Untitled Project'}</td>
                    <td className="px-3 py-2 text-sm">{displayUser}</td>
                    <td className="px-3 py-2 text-sm">{req.department_name || 'General'}</td>
                    <td className="px-3 py-2 text-sm">{formatDate(req.requisition_date)}</td>
                    <td className="px-3 py-2 text-sm">{displaySupplier}</td>
                    <td className="px-3 py-2 text-sm font-medium text-green-700">{formatQuoteValue(req.quotation_total, req.quotation_vat)}</td>
                    <td className="px-3 py-2 text-sm">{getStatusDisplay(req)}</td>
                    <td className="px-3 py-2 text-sm">
                      <Link
                        to={`/dashboard/requisitions/${req.requisition_id}`}
                        className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Enhanced Pagination */}
        {totalCount > 0 && (
          <div className="bg-gray-50 px-3 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-4">
              <div className="text-gray-500">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
              </div>
              
              {/* Page Size Selector */}
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Show:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                  className="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span className="text-gray-500">per page</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Previous
              </button>
              
              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {(() => {
                  const pageNumbers = [];
                  
                  // Show max 5 page numbers
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);
                  
                  // Adjust start if we're near the end
                  if (endPage - startPage < 4) {
                    startPage = Math.max(1, endPage - 4);
                  }
                  
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1.5 border rounded text-xs font-medium ${
                          i === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  return pageNumbers;
                })()}
              </div>
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RequisitionsList;
